import React, { useEffect, useState } from "react";

import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  Modal,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { isEmpty } from "lodash";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import {
  UpdateLifestyleInput,
  ClientLifestyleFragment,
  StressLevel,
  PreferredCoaching,
  Frequency,
  PhysicalActivityLevel2,
} from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { humanize } from "@/utils/string";

type EditLifestyleModalType = {
  visible: boolean | undefined;
  onClose: () => void;
  showSnackBar: (isSuccessful: boolean) => void;
  lifestyle: ClientLifestyleFragment | null | undefined;
  stressLevelDescriptions: { [key: string]: string };
};

type FormValues = {
  preferredCoachingType: string | null;
  physicalActivityLevel2: string;
  stressLevel: string;
  sportsDescription: string | undefined;
  sportsFrequency: string | null;
  doYouDrink: boolean | undefined;
  alcoholIntakeFrequency: string;
  averageHoursOfSleep: number | undefined;
  bowelMovement: string | undefined;
  doYouCook: boolean | undefined;
  workoutDescription: string | undefined;
  workoutFrequency: string | null;
};

const validationSchema = yup.object().shape({
  preferredCoachingType: yup
    .string()
    .nullable()
    .transform((value) => (isEmpty(value) ? null : value))
    .oneOf([...Object.values(PreferredCoaching), null]),
  physicalActivityLevel2: yup
    .string()
    .nullable()
    .transform((value) => (isEmpty(value) ? null : value))
    .oneOf([...Object.values(PhysicalActivityLevel2), null]),
  stressLevel: yup
    .string()
    .nullable()
    .transform((value) => (isEmpty(value) ? null : value))
    .oneOf([...Object.values(StressLevel), null]),
  sportsDescription: yup.string().nullable(),
  sportsFrequency: yup
    .string()
    .nullable()
    .transform((value) => (isEmpty(value) ? null : value))
    .oneOf([...Object.values(Frequency), null]),
  doYouDrink: yup.boolean().nullable(),
  alcoholIntakeFrequency: yup
    .string()
    .nullable()
    .transform((value) => (isEmpty(value) ? null : value))
    .oneOf([...Object.values(Frequency), null]),
  averageHoursOfSleep: yup
    .number()
    .integer("Hours of sleep must have no decimal values")
    .min(1)
    .max(23)
    .typeError("Hours of Sleep must be a number")
    .label("Hours of Sleep")
    .nullable(),
  bowelMovement: yup.string().nullable(),
  doYouCook: yup.boolean().nullable(),
  workoutDescription: yup.string().nullable(),
  workoutFrequency: yup
    .string()
    .nullable()
    .transform((value) => (isEmpty(value) ? null : value))
    .oneOf([...Object.values(Frequency), null]),
});

const useStyles = makeStyles()((theme) => ({
  root: {
    overflowY: "auto",
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    margin: theme.spacing(18, 36),
    backgroundColor: colors.white,
    overflowY: "auto",
    padding: theme.spacing(2, 2),
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(2, 2, 0, 2),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  modalContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(12),
  },
  fields: {
    width: "45%",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  save: {
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    marginBottom: "24px",
    marginTop: "28px",
  },
  error: {
    fontSize: "12px",
    textAlign: "center",
    color: "#963832",
  },
  description: {
    fontSize: "12px !important",
    background: colors.black,
  },
}));
const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F9FB",
    },
    width: "100%",
    marginTop: "2px",
  },
});
const StyledSaveButton = withStyles(Button, {
  root: {
    backgroundColor: colors.primary,
    width: "132px",
    height: "52px",
    "&:hover": {
      backgroundColor: colors.primary,
    },
  },
});
const StyledDescriptionField = withStyles(TextField, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F9FB",
    },
    "& .MuiInputBase-root": {
      fontSize: "12px",
    },
    width: "100%",
    marginTop: "10px",
  },
});

const updateLifestyleMutation = graphql(`
  mutation UpdateLifestyle($input: UpdateLifestyleInput!) {
    updateLifestyle(input: $input) {
      errors {
        fullMessage
      }
      lifestyle {
        id
        ...ClientLifestyle
      }
    }
  }
`);

const EditLifestyleModal: React.FC<EditLifestyleModalType> = ({
  visible,
  onClose,
  showSnackBar,
  lifestyle,
  stressLevelDescriptions,
}) => {
  const { classes } = useStyles();
  const { clientId } = useParams<{ clientId: string }>();
  const modalVisibility = visible !== undefined ? visible : false;
  const [updateLifestyle] = useMutation(updateLifestyleMutation);
  const stressLevelOptions = [
    StressLevel.Acute,
    StressLevel.Episodic,
    StressLevel.Chronic,
  ];

  const frequencyOptions = [
    Frequency.Daily,
    Frequency.Weekly,
    Frequency.Monthly,
  ];
  const [_stressLevelDescription, setStressLevelDescription] = useState("");

  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    reValidateMode: "onBlur",
    mode: "onBlur",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      preferredCoachingType: "",
      physicalActivityLevel2: "",
      stressLevel: "",
      sportsDescription: "",
      sportsFrequency: "",
      doYouDrink: false,
      alcoholIntakeFrequency: "",
      averageHoursOfSleep: 0,
      bowelMovement: "",
      doYouCook: false,
      workoutDescription: "",
      workoutFrequency: "",
    },
  });

  useEffect(() => {
    setValue("alcoholIntakeFrequency", lifestyle?.alcoholIntakeFrequency ?? "");
    setValue("averageHoursOfSleep", lifestyle?.averageHoursOfSleep ?? 0);
    setValue("bowelMovement", lifestyle?.bowelMovement ?? "");
    setValue("doYouCook", lifestyle?.doYouCook ?? false);
    setValue("doYouDrink", lifestyle?.doYouDrink ?? false);
    setValue(
      "physicalActivityLevel2",
      lifestyle?.physicalActivityLevel2?.toUpperCase() ?? ""
    );
    setValue(
      "preferredCoachingType",
      lifestyle?.preferredCoachingType?.toUpperCase() ?? ""
    );
    setValue("sportsDescription", lifestyle?.sportsDescription ?? "");
    setValue("sportsFrequency", lifestyle?.sportsFrequency ?? "");
    setValue("stressLevel", lifestyle?.stressLevel?.toUpperCase() ?? "");
    setValue("workoutDescription", lifestyle?.workoutDescription ?? "");
    setValue("workoutFrequency", lifestyle?.workoutFrequency ?? "");
    handleChangeStressLevel(lifestyle?.stressLevel?.toUpperCase() ?? "");
  }, [lifestyle]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    const input: UpdateLifestyleInput = {
      clientId: clientId,
      attributes: {
        preferredCoachingType: formValues?.preferredCoachingType,
        doYouDrink: formValues?.doYouDrink,
        alcoholIntakeFrequency: formValues?.alcoholIntakeFrequency,
        averageHoursOfSleep: formValues?.averageHoursOfSleep,
        bowelMovement: formValues?.bowelMovement,
        physicalActivityLevel2: formValues?.physicalActivityLevel2,
        doYouCook: formValues?.doYouCook,
        sportsDescription: formValues?.sportsDescription,
        sportsFrequency: formValues?.sportsFrequency,
        stressLevel: formValues?.stressLevel,
        workoutDescription: formValues?.workoutDescription,
        workoutFrequency: formValues?.workoutFrequency,
      },
    };

    try {
      await updateLifestyle({ variables: { input: input } });
      showSnackBar(true);
      onClose();
    } catch (e) {
      showSnackBar(false);
    }
  };

  const isDrinking = watch("doYouDrink");

  const handleChangeStressLevel = (value: string) => {
    setStressLevelDescription(stressLevelDescriptions[value]);
  };

  return (
    <Modal open={modalVisibility} onClose={onClose} className={classes.root}>
      <Box className={classes.modalBody}>
        <Box className={classes.modalHeader}>
          <Typography className={classes.modalHeaderText}>
            Edit Lifestyle Information
          </Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display={"flex"} flexWrap="wrap">
            {/* Preferred coaching type */}
            <Controller
              name="preferredCoachingType"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Coaching type
                    </Typography>
                    <StyledTextField select {...field}>
                      <MenuItem value={PreferredCoaching.Buddy}>
                        {humanize(PreferredCoaching.Buddy)}
                        {" (Invites opinion and personal decision making)"}
                      </MenuItem>
                      <MenuItem value={PreferredCoaching.Cheerleader}>
                        {humanize(PreferredCoaching.Cheerleader)}
                        {" (Provides cheerful encouragements)"}
                      </MenuItem>
                      <MenuItem value={PreferredCoaching.DrillSergeant}>
                        {humanize(PreferredCoaching.DrillSergeant)}
                        {" (Strict monitoring & coaching)"}
                      </MenuItem>
                      <MenuItem value={PreferredCoaching.Teacher}>
                        {humanize(PreferredCoaching.Teacher)}
                        {" (Provides detailed program)"}
                      </MenuItem>
                    </StyledTextField>
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            {/* Physical activity level */}
            <Controller
              name="physicalActivityLevel2"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Physical Activity Level
                    </Typography>
                    <StyledTextField select {...field}>
                      <MenuItem value={PhysicalActivityLevel2.Sedentary}>
                        {humanize(PhysicalActivityLevel2.Sedentary)}
                        {" (Little to no regular exercise)"}
                      </MenuItem>
                      <MenuItem value={PhysicalActivityLevel2.LightActive}>
                        {humanize(PhysicalActivityLevel2.LightActive)}
                        {
                          " (Intensive exercise for at least 20 minutes 1 to 3 times per week)"
                        }
                      </MenuItem>
                      <MenuItem value={PhysicalActivityLevel2.ModeratelyActive}>
                        {humanize(PhysicalActivityLevel2.ModeratelyActive)}
                        {
                          " (Intensive exercise for at least 30 to 60 minutes 3 to 4 times per week)"
                        }
                      </MenuItem>
                      <MenuItem value={PhysicalActivityLevel2.VeryActive}>
                        {humanize(PhysicalActivityLevel2.VeryActive)}
                        {
                          " (Intensive exercise for 60 minutes or greater 5 to 7 days per week)"
                        }
                      </MenuItem>
                      <MenuItem value={PhysicalActivityLevel2.ExtremeActive}>
                        {humanize(PhysicalActivityLevel2.ExtremeActive)}
                        {
                          " (Exceedingly active and/or very demanding activities)"
                        }
                      </MenuItem>
                    </StyledTextField>
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            <Controller
              name="sportsFrequency"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Sports Frequency
                    </Typography>
                    <StyledTextField select {...field}>
                      {frequencyOptions.map((e) => (
                        <MenuItem key={e} value={e}>
                          {humanize(e)}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            {/* Sports description */}
            <Controller
              name="sportsDescription"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Sports
                    </Typography>
                    <StyledTextField {...field} />
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            {/* Hours of sleep */}
            <Controller
              name="averageHoursOfSleep"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Hours of Sleep
                    </Typography>
                    <StyledTextField type="number" {...field} />
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            {/* Do you drink */}
            <Controller
              name="doYouDrink"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Do you drink?
                    </Typography>
                    <StyledTextField
                      select
                      {...field}
                      onChange={(event) => {
                        const newValue =
                          event.target.value === "true" ? true : false;
                        field.onChange(newValue);
                      }}
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </StyledTextField>
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            {/* Alcohol frequency */}
            {isDrinking ? (
              <Controller
                name="alcoholIntakeFrequency"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <Box className={classes.fields}>
                      <Typography variant="subtitle1" color="textPrimary">
                        Alcohol intake frequency
                      </Typography>
                      <StyledTextField select {...field}>
                        {frequencyOptions.map((e) => (
                          <MenuItem key={e} value={e}>
                            {humanize(e)}
                          </MenuItem>
                        ))}
                      </StyledTextField>
                      <Typography className={classes.error} variant="subtitle1">
                        {error?.message}
                      </Typography>
                    </Box>
                  );
                }}
              />
            ) : null}
            {/* Di you cook */}
            <Controller
              name="doYouCook"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Do you cook?
                    </Typography>
                    <StyledTextField
                      select
                      {...field}
                      onChange={(event) => {
                        const newValue =
                          event.target.value === "true" ? true : false;
                        field.onChange(newValue);
                      }}
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </StyledTextField>
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            {/* Workout description */}
            <Controller
              name="workoutDescription"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Workout description
                    </Typography>
                    <StyledTextField {...field} />
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            {/* Workout frequency */}
            <Controller
              name="workoutFrequency"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Workout frequency
                    </Typography>
                    <StyledTextField select {...field}>
                      {frequencyOptions.map((e) => (
                        <MenuItem key={e} value={e}>
                          {humanize(e)}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            {/* Stress level */}
            <Controller
              name="stressLevel"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Stress Level
                    </Typography>
                    <RadioGroup
                      {...field}
                      onChange={(event) => {
                        handleChangeStressLevel(event.target.value);
                        field.onChange(event.target.value);
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {stressLevelOptions.map((e) => (
                          <FormControlLabel
                            label={humanize(e)}
                            key={e}
                            value={e}
                            control={<Radio color="primary" />}
                          />
                        ))}
                      </Box>
                    </RadioGroup>
                    <StyledDescriptionField
                      {...field}
                      value={_stressLevelDescription}
                      multiline
                      rows={5}
                      disabled
                    />
                    <Typography className={classes.error} variant="subtitle1">
                      {error?.message}
                    </Typography>
                  </Box>
                );
              }}
            />
            {/* Stress level */}
          </Box>

          <div className={classes.save}>
            <StyledSaveButton type="submit">Update</StyledSaveButton>
          </div>
        </form>
      </Box>
    </Modal>
  );
};
export default EditLifestyleModal;
